import { useTribalContext } from '@/context/tribal';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import {
  FinanceCalculationsUnavailable,
  PackageCard,
} from '@tribal-worldwide-london/ui-component-library-vwg';
import Accordion from '@/modules/components/Accordion/Accordion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetCalculationForPcpProductRequestDto,
  GetProductsForCapCodeAndPriceRequestDto,
  Product,
  Vehicle,
} from '@tribal-worldwide-london/fa-finance-service-vwg';
import { Box } from '@/modules/components';
import {
  DesignTokenSizeEnum,
  Text,
  TextTag,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import {
  GetCarConfigurationFromStockOrderNumberRequestDto,
  GetConfigurationFromMarketingShortCodeRequestDto,
} from '@tribal-worldwide-london/fa-car-service-vwg';
import {
  FinanceDeeplinkUrl,
  GetFinanceDeepLinkUrlDTO,
} from '@tribal-worldwide-london/fa-agent-service-vwg';
import useDeepLinkGenerator from '@/hooks/useDeepLinkGenerator';
import { SectionNames } from '@/analytics/sectionNames';
import {
  handleAccordionClose,
  handleAccordionOpen,
} from '@/analytics/eventHelpers/accordion';
import {
  EventCommonProps,
  performVWBasicButtonClick,
  performVWBasicErrorMessageLoad,
  performVWBasicLayerLoad,
  performVWBasicLinkClick,
  performVWBasicVideoPlayerPauseClick,
  performVWBasicVideoPlayerPlayClick,
  performVWInfoButtonCLick,
  performVWBasicLayerClose,
  performVWBasicVideoPlayerMilestoneReached,
} from '@/analytics/events';
import { getAnalyticsConfigurationFromCarConfiguration } from '@/analytics/utils';
import { RetailerFinanceTerms } from './YourFinanceCalculation.styles';
import { getBespokeSpecItemsCodes, getFinanceCtaUnavailableUrl, isStockCar } from '@/modules/utils/storeUtils';
import { ApiResponseDTO } from '@tribal-worldwide-london/fa-api-service';
import { useParams } from 'react-router-dom';

interface YourFinanceCalculationProps {
  activeConfiguration: GlobalStoreStateConfiguration;
  index: number;
}

function YourFinanceCalculation({
  activeConfiguration,
}: YourFinanceCalculationProps) {
  const { contentManager, carService, financeService } = useTribalContext();
  const [carData, setCarData] = useState<any>();
  const [products, setProducts] = useState<Product[]>([]);
  const { retailers } = useGlobalStore((state) => state.app);
  const { mscId, orderNumberId } = useParams();

  useEffect(() => {
    if (activeConfiguration?.orderNumber === orderNumberId) {
      const bespokeItemsCodes = getBespokeSpecItemsCodes(activeConfiguration.bespokeSpecItems || []);
      const req1 = new GetCarConfigurationFromStockOrderNumberRequestDto(
        activeConfiguration.orderNumber!,
        true,
        false,
        bespokeItemsCodes
      );

      carService
        .getCarConfigurationFromStockOrderNumber(req1)
        .then((res: any) => {
          if (res?.data) {
            setCarData(res.data);
          } else {
            return Promise.reject(res.error);
          }
        });
    } else if (activeConfiguration?.marketingShortCode === mscId){
      const req1 = new GetConfigurationFromMarketingShortCodeRequestDto(
        activeConfiguration?.marketingShortCode!,
        true,
        false
      );

      carService
        .getCarConfigurationFromMakertingShortCode(req1)
        .then((res: any) => {
          if (res?.data) {
            setCarData(res.data);
          } else {
            return Promise.reject(res.error);
          }
        });
    }
  }, [activeConfiguration.orderNumber, activeConfiguration.marketingShortCode]);

  useEffect(() => {
    if (carData) {
      const price =
        carData.price?.finance?.personalPrice -
        (activeConfiguration.pdbData?.pricing?.allowance || 0);

      const vehicle: Vehicle = {
        capCode: carData.summary?.capCode?.value,
        price,
      };
      financeService
        .getProductsForCapCodeAndPrice(
          new GetProductsForCapCodeAndPriceRequestDto('PERSONAL', [vehicle])
        )
        .then((res: any) => {
          if (res.success) {
            setProducts(res.data);
          } else {
            return Promise.reject(res.error);
          }
        });
    }
  }, [carData]);

  return (
    <>
      <Box
        mb={DesignTokenSizeEnum.static350}
        b960={{ mb: DesignTokenSizeEnum.static515 }}
      >
        <Box mb={DesignTokenSizeEnum.static250}>
          <Text tag={TextTag.h2} appearance={TokenTextAppearance.headline400}>
            {contentManager.get(
              'your-finance-calculation-section-title-normal'
            )}
            <strong>
              {' '}
              {contentManager.get(
                'your-finance-calculation-section-title-bold'
              )}
            </strong>
          </Text>
        </Box>
        <Text appearance={TokenTextAppearance.headline200}>
          {contentManager.get('your-finance-calculation-section-subtitle')}
        </Text>
      </Box>

      {carData &&
        activeConfiguration.finance?.map((calculation, index) => {
          if (calculation.parameters == null || products.length === 0) {
            return (
              <FinanceCalculation
                key={index}
                activeConfiguration={activeConfiguration}
                calculation={[]}
                index={index}
                financePersonalPrice={carData?.price?.finance?.personalPrice || 0}
                displayPersonalPrice={carData?.price?.display?.personalPrice}
                error={true}
                products={products}
                capCode={carData.summary?.capCode?.value}
              />
            );
          }

          return (
            <FinanceCalculation
              key={index}
              activeConfiguration={activeConfiguration}
              calculation={calculation}
              index={index}
              financePersonalPrice={carData?.price?.finance?.personalPrice}
              displayPersonalPrice={carData?.price?.display?.personalPrice}
              error={false}
              products={products}
              capCode={carData.summary?.capCode?.value}
            />
          );
        })
      }

      <Box mt={DesignTokenSizeEnum.static400}>
        <RetailerFinanceTerms appearance={TokenTextAppearance.label150}>
          {retailers[0]?.fcaTermsAndConditions}
        </RetailerFinanceTerms>
      </Box>
    </>
  );
}

interface FinanceCalculationProps {
  activeConfiguration: GlobalStoreStateConfiguration;
  calculation: any;
  index: number;
  financePersonalPrice: number;
  displayPersonalPrice: number;
  error: boolean;
  products: Product[];
  capCode: string;
}

export const FinanceCalculation = ({
  activeConfiguration,
  calculation,
  index,
  financePersonalPrice,
  displayPersonalPrice,
  error,
  products,
  capCode
}: FinanceCalculationProps) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(
    index === 0 ? true : false
  );
  const [data, setData] = useState<any>();
  const [hasError, setHasError] = useState<boolean>(false);
  const [isFinanceDeeplinkUrlLoading, setIsFinanceDeeplinkUrlLoading] =
    useState<boolean>(false);
  const {
    contentManager,
    analyticsBuilder,
    analyticsManager,
    financeService,
    configManager,
    agentService,
  } = useTribalContext();

  const { retailers } = useGlobalStore((state) => state.app);

  // Analytics data object
  const analyticsCommonFields = useMemo(
    () =>
      (sectionName: SectionNames): EventCommonProps => ({
        sectionName,
        analyticsManager,
        analyticsBuilder,
        retailers,
        configuration: activeConfiguration,
      }),
    [analyticsManager, analyticsBuilder, retailers, activeConfiguration]
  );

  const handleToggleAccordion = useCallback(() => {
    const groupItemName = `${contentManager.get(
      'single-page-finance-accordion-title'
    )} ${index}`;

    if (!isAccordionOpen) {
      handleAccordionOpen(
        analyticsCommonFields(
          SectionNames.SINGLE_PAGE_YOUR_FINANCE_CALCULATION
        ),
        {
          groupItemName,
          groupItemState: 'open',
        }
      );
    } else {
      handleAccordionClose(
        analyticsCommonFields(
          SectionNames.SINGLE_PAGE_YOUR_FINANCE_CALCULATION
        ),
        {
          groupItemName,
          groupItemState: 'closed',
        }
      );
    }

    setIsAccordionOpen(!isAccordionOpen);
  }, [isAccordionOpen, contentManager, index, analyticsCommonFields]);

  const handleClickFinanceCtaUnavailable = useCallback(() => {
    performVWBasicLinkClick({
      label: contentManager.get(
        'finance-calculations-unavailable-cta-link-text'
      ),
      sectionName: SectionNames.SINGLE_PAGE_YOUR_FINANCE_CALCULATION,
      configuration: activeConfiguration,
      retailers,
      analyticsManager,
      analyticsBuilder,
    });
    window.open(
      getFinanceCtaUnavailableUrl(
        configManager.get('urls.offersFinanceFA'),
        activeConfiguration
      ),
      '_self'
    );
  }, [
    analyticsCommonFields,
    activeConfiguration,
    configManager,
  ]);

  const handleClickFinanceCtaSuccess = async () => {
    const url = await fetchDeeplinkUrl();
    performVWBasicLinkClick({
      label: contentManager.get(
        'finance-calculations-unavailable-cta-link-text'
      ),
      sectionName: SectionNames.SINGLE_PAGE_YOUR_FINANCE_CALCULATION,
      configuration: activeConfiguration,
      retailers,
      analyticsManager,
      analyticsBuilder,
    });
    if (url) {
      window.open(url, '_self');
    }
  };

  const analyticsFunctions = {
    clickFinanceExplained: () => {
      performVWInfoButtonCLick(
        analyticsCommonFields(SectionNames.SINGLE_PAGE_YOUR_FINANCE_CALCULATION)
      );
    },
    videoPauseCallback: (e, video) => {
      const eventData = {
        playerName: e.playerName ?? null,
        title: 'Volkswagen - Car finance made simple',
        id: video ?? null,
        platform: e.platform ?? null,
        url: e.url ?? null,
        time_played: e.timePlayed ?? null,
        length: e.length ?? null,
        playReason: null,
        viewable: null,
        resolution: null,
        milestone: null,
      };

      performVWBasicVideoPlayerPauseClick({
        videoFields: eventData,
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION
        ),
        ...getAnalyticsConfigurationFromCarConfiguration(activeConfiguration),
      });
    },
    videoPlayCallback: (e, video) => {
      const eventData = {
        playerName: e.playerName ?? null,
        title: 'Volkswagen - Car finance made simple',
        id: video ?? null,
        platform: e.platform ?? null,
        url: e.url ?? null,
        time_played: e.timePlayed ?? null,
        length: e.length ?? null,
        playReason: null,
        viewable: null,
        resolution: null,
        milestone: null,
      };

      performVWBasicVideoPlayerPlayClick({
        videoFields: eventData,
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION
        ),
        ...getAnalyticsConfigurationFromCarConfiguration(activeConfiguration),
      });
    },
    videoMilestoneCallback: (e, video, milestone) => {
      const eventData = {
        playerName: e.playerName ?? null,
        title: 'Volkswagen - Car finance made simple',
        id: video ?? null,
        platform: e.platform ?? null,
        url: e.url ?? null,
        time_played: e.timePlayed ?? null,
        length: e.length ?? null,
        playReason: null,
        viewable: null,
        resolution: null,
        milestone: null,
      };

      performVWBasicVideoPlayerMilestoneReached({
        videoFields: eventData,
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION
        ),
        ...getAnalyticsConfigurationFromCarConfiguration(activeConfiguration),
      });
    },
    clickFindOutMore: (ctaLink: string, ctaText: string) => {
      performVWBasicButtonClick({
        ...analyticsCommonFields(
          SectionNames.SINGLE_PAGE_YOUR_FINANCE_CALCULATION
        ),
        label: ctaText,
      });
    },
    InfoMessageLoadForConfiguration: () => {
      performVWBasicLayerLoad({
        ...analyticsCommonFields(
          SectionNames.SINGLE_PAGE_YOUR_FINANCE_CALCULATION
        ),
      });
    },
    closeClickForConfiguration: () => {
      performVWBasicLayerClose({
        ...analyticsCommonFields(
          SectionNames.SINGLE_PAGE_YOUR_FINANCE_CALCULATION
        ),
      });
    },
  };

  const renderCalculationUnavailable = useCallback(
    () => (
      <FinanceCalculationsUnavailable
        title={contentManager.get('finance-calculations-unavailable-cta-title')}
        subtitle={contentManager.get(
          'finance-calculations-unavailable-cta-subtitle'
        )}
        linkText={contentManager.get(
          'finance-calculations-unavailable-cta-link-text'
        )}
        handleFinanceCta={handleClickFinanceCtaUnavailable}
        borderRadius="8px"
      />
    ),
    [contentManager, handleClickFinanceCtaUnavailable]
  );

  useEffect(() => {
    if (error || hasError) {
      performVWBasicErrorMessageLoad({
        ...analyticsCommonFields,
        error: {
          message: contentManager.get(
            'finance-calculations-unavailable-cta-title'
          ),
        },
        analyticsManager,
        analyticsBuilder,
      });
    }
  }, [error, hasError]);

  useEffect(() => {
    if (!error) {
      const price =
        financePersonalPrice -
        (activeConfiguration.pdbData?.pricing?.allowance || 0);

      const fProduct = products.find(
        (product) => product.label === calculation.productLabel
      );
      if (fProduct) {
        const req = new GetCalculationForPcpProductRequestDto(
          capCode,
          fProduct.productId!,
          price,
          calculation.parameters.deposit!,
          calculation.parameters.monthlyPayment!,
          calculation.parameters.periodOfMonths,
          calculation.parameters.annualMileage
        );
        financeService
          .getCalculationForPcpProduct(req)
          .then((res: any) => {
            if (res.success) {
              setData(res.data);
              setHasError(false);
            } else {
              setHasError(true);
            }
          })
          .catch((error: any) => {
            setHasError(true);
          });
      } else {
        setHasError(true);
      }
    }
  }, [
    calculation.productId!,
    activeConfiguration.pdbData?.pricing?.allowance,
    financePersonalPrice,
  ]);

  const fetchDeeplinkUrl = async () => {
    const vehicleType = isStockCar(activeConfiguration)
      ? 'STOCK'
      : 'CONFIGURATION';
    const vehicleId = isStockCar(activeConfiguration)
      ? activeConfiguration.orderNumber
      : activeConfiguration.marketingShortCode;
    let productId = calculation.productId || '';
    let months = calculation.parameters.periodOfMonths;
    let mileage = calculation.parameters.annualMileage;
    let deposit = calculation.parameters.deposit;
    const bespokeItemsCodes = getBespokeSpecItemsCodes(
      activeConfiguration.bespokeSpecItems || []
    );

    try {
      setIsFinanceDeeplinkUrlLoading(true);
      const response: ApiResponseDTO<FinanceDeeplinkUrl> =
        await agentService.getRedirectFinanceDeepLinkUrl(
          new GetFinanceDeepLinkUrlDTO(
            vehicleType,
            vehicleId || '',
            'PCP',
            !calculation.productLabel ? productId : '',
            calculation.productLabel,
            months!,
            mileage!,
            deposit,
            undefined,
            undefined,
            bespokeItemsCodes
          )
        );
      if (response.success) {
        return response.data ? response.data.url : '';
      }
      return '';
    } catch {
      return '';
    } finally {
      setIsFinanceDeeplinkUrlLoading(false);
    }
  };

  return (
    <Accordion
      id="calculation"
      title={
        <strong>
          {contentManager.get('single-page-finance-accordion-title')}{' '}
          {activeConfiguration.finance &&
          activeConfiguration.finance?.length > 1
            ? index + 1
            : ''}
        </strong>
      }
      subtitle=""
      isOpen={isAccordionOpen}
      onToggle={handleToggleAccordion}
    >
      {error ? (
        renderCalculationUnavailable()
      ) : (
        <PackageCard
          modelTitle={activeConfiguration.modelTitle}
          productType=""
          derivativeTitle={activeConfiguration.pdbData?.derivative?.title || ''}
          productNameWithDescription={data?.calculation?.description}
          offerTypeText=""
          financeCalcGrid={data?.calculation?.details || []}
          disclaimerText={data?.calculation?.shortDisclaimer}
          terms={{
            closedButtonText: contentManager.get('single-page-open-terms'),
            openButtonText: contentManager.get('single-page-close-terms'),
            children: `${data?.calculation?.disclaimer}`,
          }}
          offerNumber=""
          financeOfferType=""
          error={hasError}
          renderErrorComponent={renderCalculationUnavailable}
          representativeExampleText={contentManager.get(
            'representative-example-text'
          )}
          optionalExtrasText={
            financePersonalPrice !== displayPersonalPrice
              ? contentManager.get('optional-extras-text')
              : ''
          }
          optionalExtrasSectionId={contentManager.get('extras-section-id')}
          financeCtaTitle={contentManager.get('finance-cta-title')}
          financeCtaText={contentManager.get('finance-cta-text')}
          handleFinanceCta={handleClickFinanceCtaSuccess}
          isFinanceDeeplinkUrlLoading={isFinanceDeeplinkUrlLoading}
          financeExplainedProductType={contentManager.get(
            'your-finance-calculation-section-finance-explained-product-type'
          )}
          financeExplainedCtaAriaText={contentManager.get(
            'your-finance-calculation-section-finance-explained-cta-aria-text'
          )}
          analyticsFunctions={analyticsFunctions}
          financeExplainedProductTabs={[
            {
              title: `${contentManager.get(
                'your-finance-calculation-section-finance-explained-product-tabs-title'
              )}`,
              titleBold: `${contentManager.get(
                'your-finance-calculation-section-finance-explained-product-tabs-title-bold'
              )}`,
              video: `${contentManager.get(
                'your-finance-calculation-section-finance-explained-product-tabs-video'
              )}`,
              bodySubtitle: `${contentManager.get(
                'your-finance-calculation-section-finance-explained-product-tabs-body-subtitle'
              )}`,
              body: `${contentManager.get(
                'your-finance-calculation-section-finance-explained-product-tabs-body'
              )}`,
              ctaText: `${contentManager.get(
                'your-finance-calculation-section-finance-explained-product-tabs-cta-text'
              )}`,
              ctaLink: `${contentManager.get(
                'your-finance-calculation-section-finance-explained-product-tabs-cta-link'
              )}`,
            },
          ]}
        />
      )}
    </Accordion>
  );
};

export default YourFinanceCalculation;
